import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

/** Gedeelde bouwstenen voor de donkere, full-screen opleider-onboarding. */

export const Kaart: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <section className={`rounded-2xl border border-white/10 bg-white/[0.03] p-6 shadow-2xl shadow-black/40 sm:p-8 ${className}`}>
    {children}
  </section>
);

export const StapKop: React.FC<{ nummer: number; titel: string; intro: string }> = ({ nummer, titel, intro }) => {
  const { t } = useTranslation('pages');
  return (
  <header className="mb-6">
    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-400">
      {t('opleiderWizard.stapVan', { defaultValue: 'Stap {{n}} / 5', n: nummer })}
    </p>
    <h2 className="mt-1 text-2xl font-bold text-white">{titel}</h2>
    <p className="mt-2 text-sm leading-relaxed text-slate-400">{intro}</p>
  </header>
  );
};

interface VeldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  icoon?: React.ReactNode;
  hint?: string;
  fout?: string | null;
}

export const Veld: React.FC<VeldProps> = ({ label, icoon, hint, fout, id, className = '', ...rest }) => {
  const veldId = id ?? rest.name;
  return (
    <div className={className}>
      <label htmlFor={veldId} className="mb-1.5 block text-sm font-medium text-slate-200">
        {label}
      </label>
      <div className="relative">
        {icoon && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">{icoon}</span>}
        <input
          id={veldId}
          aria-invalid={!!fout}
          aria-describedby={fout ? `${veldId}-fout` : hint ? `${veldId}-hint` : undefined}
          className={`w-full rounded-lg border bg-slate-900/70 py-2.5 pr-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-sky-400 ${
            icoon ? 'pl-10' : 'pl-3'
          } ${fout ? 'border-rose-500/70' : 'border-white/10'}`}
          {...rest}
        />
      </div>
      {fout ? (
        <p id={`${veldId}-fout`} className="mt-1 text-xs text-rose-400">{fout}</p>
      ) : hint ? (
        <p id={`${veldId}-hint`} className="mt-1 text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
};

interface KnopProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  bezig?: boolean;
  variant?: 'primair' | 'secundair';
  groot?: boolean;
}

export const Knop: React.FC<KnopProps> = ({ bezig, variant = 'primair', groot, children, className = '', disabled, ...rest }) => (
  <button
    disabled={disabled || bezig}
    className={`inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0F17] disabled:cursor-not-allowed disabled:opacity-50 ${
      groot ? 'px-8 py-4 text-base' : 'px-5 py-3 text-sm'
    } ${
      variant === 'primair'
        ? 'bg-sky-400 text-slate-950 hover:bg-sky-300'
        : 'border border-white/15 bg-transparent text-slate-200 hover:bg-white/5'
    } ${className}`}
    {...rest}
  >
    {bezig && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
    {children}
  </button>
);

export const Melding: React.FC<{ soort?: 'fout' | 'info' | 'ok'; children: React.ReactNode }> = ({ soort = 'fout', children }) => {
  const stijl = {
    fout: 'border-rose-500/30 bg-rose-500/10 text-rose-200',
    info: 'border-sky-400/30 bg-sky-400/10 text-sky-100',
    ok: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-100',
  }[soort];
  return (
    <div role={soort === 'fout' ? 'alert' : 'status'} className={`rounded-lg border px-4 py-3 text-sm ${stijl}`}>
      {children}
    </div>
  );
};
